import React, { useState } from 'react';
import FormInput from './FormInput';
import { useFormValidation } from '../hooks/useFormValidation';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import LoadingSpinner from './LoadingSpinner';

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { errors, touched, validateAllFields, handleBlur, handleChange, resetValidation } =
    useFormValidation({
      name: [
        {
          required: true,
          message: 'Por favor, insira o seu nome',
        },
        {
          minLength: 2,
          message: 'O nome deve ter pelo menos 2 caracteres',
        },
      ],
      email: [
        {
          required: true,
          message: 'Por favor, insira o seu email',
        },
        {
          email: true,
          message: 'Por favor, insira um email válido',
        },
      ],
      phone: [
        {
          phone: true,
          message: 'Por favor, insira um número de telefone válido',
        },
      ],
      message: [
        {
          required: true,
          message: 'Por favor, insira a sua mensagem',
        },
        {
          minLength: 10,
          message: 'A mensagem deve ter pelo menos 10 caracteres',
        },
        {
          maxLength: 1000,
          message: 'A mensagem não pode exceder 1000 caracteres',
        },
      ],
    });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    handleChange(name, value);
  };

  const handleInputBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    handleBlur(name, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields
    const isValid = validateAllFields(formData);

    if (!isValid) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    setIsSubmitting(true);

    try {
      // Replace YOUR_FORM_ID with your actual Formspree form ID
      const response = await fetch('https://formspree.io/f/YOUR_FORM_ID', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Mensagem enviada com sucesso! Entraremos em contato em breve.', {
          duration: 5000,
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
        });
        resetValidation();
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      toast.error('Erro ao enviar mensagem. Por favor, tente novamente ou contacte-nos diretamente.', {
        duration: 6000,
      });
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.form
      id="contact-form"
      onSubmit={handleSubmit}
      className="max-w-2xl mx-auto space-y-6 bg-white p-8 rounded-2xl shadow-soft-lg"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <FormInput
        label="Nome"
        name="name"
        type="text"
        value={formData.name}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        error={errors.name}
        touched={touched.name}
        placeholder="O seu nome"
        required
        icon={
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        }
      />

      <FormInput
        label="Email"
        name="email"
        type="email"
        value={formData.email}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        error={errors.email}
        touched={touched.email}
        placeholder="o.seu@email.com"
        required
        icon={
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        }
      />

      <FormInput
        label="Telefone (Opcional)"
        name="phone"
        type="tel"
        value={formData.phone}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        error={errors.phone}
        touched={touched.phone}
        placeholder="+351 912 345 678"
        icon={
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
        }
      />

      <FormInput
        label="Mensagem"
        name="message"
        type="textarea"
        value={formData.message}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        error={errors.message}
        touched={touched.message}
        placeholder="Descreva como podemos ajudá-lo..."
        required
        rows={6}
      />

      <div className="text-sm text-gray-600">
        {formData.message.length}/1000 caracteres
      </div>

      <motion.button
        type="submit"
        disabled={isSubmitting}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        whileHover={!isSubmitting ? { scale: 1.02 } : {}}
        whileTap={!isSubmitting ? { scale: 0.98 } : {}}
      >
        {isSubmitting ? (
          <>
            <LoadingSpinner size="sm" />
            <span>A enviar...</span>
          </>
        ) : (
          <>
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
            <span>Enviar Mensagem</span>
          </>
        )}
      </motion.button>

      <p className="text-xs text-center text-gray-500 mt-4">
        Os seus dados são protegidos e nunca serão partilhados com terceiros.
      </p>
    </motion.form>
  );
};

export default ContactForm;
